const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.village-baidane.com/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      console.log('Making API request to:', url);
      console.log('Request config:', config);

      const response = await fetch(url, config);

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      const data = await response.json();
      console.log('Response data:', data);

      if (!response.ok) {
        console.error('API Error Response:', data);

        // Handle different error formats
        let errorMessage = 'API request failed';

        if (data.detail) {
          errorMessage = data.detail;
        } else if (data.message) {
          errorMessage = data.message;
        } else if (data.error) {
          errorMessage = data.error;
        } else if (typeof data === 'object') {
          // Handle field-specific errors (like Django validation errors)
          const fieldErrors = [];
          for (const [field, errors] of Object.entries(data)) {
            if (Array.isArray(errors)) {
              fieldErrors.push(`${field}: ${errors.join(', ')}`);
            } else if (typeof errors === 'string') {
              fieldErrors.push(`${field}: ${errors}`);
            }
          }
          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors.join('; ');
          }
        }

        // Create error object with both message and raw data
        const error = new Error(errorMessage);
        error.data = data;
        error.status = response.status;
        throw error;
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error - please check your connection');
      }
      throw error;
    }
  }

  // Health check
  async healthCheck() {
    return this.request('/health/');
  }

  // Authentication
  async login(email, password) {
    return this.request('/auth/login/', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async register(userData) {
    return this.request('/auth/register/', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async getProfile() {
    return this.request('/auth/profile/');
  }

  async updateProfile(userData) {
    return this.request('/auth/profile/', {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async changePassword(passwordData) {
    return this.request('/auth/change-password/', {
      method: 'POST',
      body: JSON.stringify(passwordData),
    });
  }

  // Bookings
  async createBooking(bookingData) {
    return this.request('/bookings/', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  }

  async getSmoobuVillas() {
    return this.request('/smoobu/villas/');
  }

  async getSmoobuQuote({ villa, checkIn, checkOut, adults, children, babies, pets, guests }) {
    return this.request('/smoobu/quote/', {
      method: 'POST',
      body: JSON.stringify({
        villa,
        check_in: checkIn,
        check_out: checkOut,
        adults,
        children,
        babies,
        pets,
        guests,
      }),
    });
  }

  async getSmoobuRates({ villa, startDate, endDate }) {
    const params = new URLSearchParams({
      villa,
      start_date: startDate,
      end_date: endDate,
    });
    return this.request(`/smoobu/rates/?${params.toString()}`);
  }

  async getSmoobuHealth() {
    return this.request('/smoobu/health/');
  }

  async getBookings(page = 1) {
    return this.request(`/bookings/?page=${page}`);
  }

  async getBooking(id) {
    return this.request(`/bookings/${id}/`);
  }

  // Contact
  async sendContactMessage(messageData) {
    return this.request('/contact/', {
      method: 'POST',
      body: JSON.stringify(messageData),
    });
  }

  // Reviews
  async getReviews() {
    return this.request('/reviews/');
  }

  async createReview(reviewData) {
    return this.request('/reviews/', {
      method: 'POST',
      body: JSON.stringify(reviewData),
    });
  }
}

// Create singleton instance
const apiService = new ApiService();

// Export specific methods for easy use
export const api = {
  // Health
  healthCheck: () => apiService.healthCheck(),
  
  // Auth
  login: (email, password) => apiService.login(email, password),
  register: (userData) => apiService.register(userData),
  getProfile: () => apiService.getProfile(),
  updateProfile: (userData) => apiService.updateProfile(userData),
  changePassword: (passwordData) => apiService.changePassword(passwordData),
  
  // Bookings
  createBooking: (bookingData) => apiService.createBooking(bookingData),
  getBookings: (page) => apiService.getBookings(page),
  getBooking: (id) => apiService.getBooking(id),
  getSmoobuVillas: () => apiService.getSmoobuVillas(),
  getSmoobuQuote: (payload) => apiService.getSmoobuQuote(payload),
  getSmoobuRates: (payload) => apiService.getSmoobuRates(payload),
  getSmoobuHealth: () => apiService.getSmoobuHealth(),
  
  // Contact
  sendContactMessage: (messageData) => apiService.sendContactMessage(messageData),
  
  // Reviews
  getReviews: () => apiService.getReviews(),
  createReview: (reviewData) => apiService.createReview(reviewData),
};

export default apiService;
